import { useEffect, useState, useRef } from "react";
import { Navigate, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { RefreshData } from "./RefreshData";
import api from "../lib/api";
import { roleLabel as roleLabelFor, roleBadge } from "../lib/rbac";
import { effective, getMyAccess } from "../lib/menuRegistry";
import { toast } from "sonner";
import FxControl from "./FxControl";
import {
  Gauge, Tray, Users as UsersIcon, ShieldCheck, Gear,
  WarningOctagon, SignOut, ListChecks, Receipt,
  MagnifyingGlass, Bell, Question, Sparkle, CaretDown, X,
  ArrowsClockwise, Truck, Table as TableIcon, Package, House, CloudArrowUp, Timer,
  CurrencyDollar, Boat, GraduationCap, ChartBar, CalendarCheck, Heartbeat, List, FileArrowDown, LinkSimple, Barcode, MapPin, Camera,
} from "@phosphor-icons/react";

// Nav is grouped by workflow. `type:"link"` renders a direct tab; `type:"menu"`
// renders a dropdown. `admin:true` sub-items show only to admins.
const NAV_GROUPS = [
  { type: "link", to: "/", label: "Dashboard", code: "DASH", icon: Gauge, testid: "nav-dashboard" },
  {
    type: "menu", label: "Forecasts", code: "FCT", icon: ChartBar, testid: "nav-forecasts-menu",
    items: [
      { section: "Walmart" },
      { to: "/forecasts/walmart/overview",   label: "Overview",   icon: ChartBar,  testid: "nav-forecasts-wm-overview" },
      { to: "/forecasts/walmart/order-view", label: "Order View", icon: TableIcon, testid: "nav-forecasts-wm-order" },
      { section: "Legrand (WM-DI)" },
      { to: "/forecasts/legrand/overview",   label: "Overview",   icon: ChartBar,  testid: "nav-forecasts-legrand-overview" },
      { to: "/forecasts/legrand/order-view", label: "Order View", icon: TableIcon, testid: "nav-forecasts-legrand-order" },
      { section: "Other Customers" },
      { to: "/forecasts/other/new",  label: "New Forecast",  icon: CloudArrowUp, testid: "nav-forecasts-other-new" },
      { to: "/forecasts/other/view", label: "View Forecast", icon: ChartBar,     testid: "nav-forecasts-other-view" },
    ],
  },
  {
    type: "menu", label: "Orders", code: "ORD", icon: Package, testid: "nav-orders-menu",
    items: [
      { section: "Intake" },
      { to: "/intake-review", label: "PO Intake Review", icon: Tray,      testid: "nav-intake-review" },
      { section: "Order Book" },
      { to: "/pos",           label: "All Purchase Orders", icon: Package,   testid: "nav-pos" },
      { to: "/open-orders",   label: "Open Orders",      icon: TableIcon, testid: "nav-open-orders" },
      { section: "AV Matics" },
      { to: "/quickbooks",    label: "QuickBooks Purchase Orders", icon: LinkSimple, testid: "nav-quickbooks" },
      { section: "SAP" },
      { to: "/sappush-soap",  label: "Create SAP SO",    icon: CloudArrowUp,   testid: "nav-create-sap-so",    admin: true },
    ],
  },
  {
    type: "menu", label: "Dispatch", code: "DSP", icon: Truck, testid: "nav-dispatch-menu",
    items: [
      { section: "Plan & Execute" },
      { to: "/dispatch-plan",  label: "Create Dispatch Plan",   icon: ListChecks, testid: "nav-dispatch-plan" },
      { to: "/dispatch-plans", label: "Dispatch Plans", icon: TableIcon,  testid: "nav-dispatch-plans" },
      { to: "/dispatch-register", label: "Dispatch Register", icon: TableIcon, testid: "nav-dispatch-register" },
      { section: "Scheduling" },
      { to: "/cfs-cutoff",     label: "CFS Cut-Off",    icon: CalendarCheck, testid: "nav-cfs-cutoff" },
      { to: "/shipping",       label: "Shipping",      icon: Boat,       testid: "nav-shipping" },
      { section: "Reports & Charges" },
      { to: "/customer-status-report", label: "Customer open order status report", icon: FileArrowDown, testid: "nav-customer-status-report" },
      { to: "/other-charges",  label: "Other Charges & Invoices", icon: Receipt, testid: "nav-other-charges" },
    ],
  },
  {
    type: "menu", label: "Logistics", code: "LOG", icon: CurrencyDollar, testid: "nav-logistics-menu",
    items: [
      { section: "Shipment Costs" },
      { to: "/logistics", label: "Capture Shipment Costs", icon: FileArrowDown, testid: "nav-logistics-capture" },
      { to: "/logistics/analysis", label: "Cost Analysis", icon: ChartBar, testid: "nav-logistics-analysis" },
      { section: "Shipping Bills" },
      { to: "/logistics/shipping-bills", label: "Upload Shipping Bill", icon: CloudArrowUp, testid: "nav-shipping-bills" },
      { to: "/logistics/shipping-bills/analytics", label: "Shipping Bill Analytics", icon: TableIcon, testid: "nav-shipping-bills-analytics" },
      { section: "Container Loading" },
      { to: "/logistics/container-loading", label: "Container Loading Photos", icon: Camera, testid: "nav-container-loading" },
    ],
  },
  {
    type: "menu", label: "Financial Receipts", code: "FAL", icon: Receipt, testid: "nav-fa-logistics-menu",
    items: [
      { to: "/fa-logistics", label: "Finance Receiving", icon: Receipt, testid: "nav-fa-logistics" },
      { to: "/fa-logistics/bulk-upload", label: "Bulk Receipt Upload", icon: CloudArrowUp, testid: "nav-fa-bulk-upload" },
      { to: "/fa-logistics/receipts-from-advice", label: "Receipts from Advice", icon: CurrencyDollar, testid: "nav-fa-receipts-advice" },
      { to: "/fa-logistics/sap-post-audit", label: "SAP Post Audit", icon: ShieldCheck, testid: "nav-fa-sap-post-audit" },
    ],
  },
  {
    type: "menu", label: "Insights", code: "INS", icon: Timer, testid: "nav-insights-menu",
    items: [
      { section: "Delivery & Risk" },
      { to: "/otd-risk", label: "OTD & Risk", icon: Timer, testid: "nav-otd-risk" },
      { to: "/short-closed", label: "Short-Closed POs", icon: WarningOctagon, testid: "nav-short-closed" },
      { section: "Order Analysis" },
      { to: "/reports", label: "Open Order Report", icon: TableIcon, testid: "nav-reports" },
      { to: "/explorer", label: "Open Order Explorer", icon: ChartBar, testid: "nav-explorer" },
      { to: "/ship-window", label: "Ask", icon: CalendarCheck, testid: "nav-ship-window" },
      { section: "Sales" },
      { to: "/sales", label: "Monthly Sales", icon: Receipt, testid: "nav-sales" },
      { to: "/sales-report", label: "Sales Report", icon: ChartBar, testid: "nav-sales-report" },
    ],
  },
  {
    type: "menu", label: "Setup", code: "SET", icon: Gear, testid: "nav-settings-menu",
    items: [
      { section: "Admin & Data" },
      { to: "/cockpit",          label: "Admin Cockpit",    icon: Gauge,          testid: "nav-cockpit",          superAdmin: true },
      { to: "/data-health",      label: "Data Health",      icon: Heartbeat,      testid: "nav-data-health",      admin: true },
      { to: "/po-reviews",       label: "PO Reviews",       icon: WarningOctagon, testid: "nav-po-reviews",        admin: true },
      { to: "/rmp-lookup",       label: "RMP Code Lookup",  icon: Barcode,        testid: "nav-rmp-lookup",       admin: true },
      { to: "/train",            label: "PO Training",      icon: GraduationCap,  testid: "nav-training",         admin: true },
      { to: "/import",           label: "Excel Import",     icon: CloudArrowUp,   testid: "nav-import",           admin: true },
      { section: "ERP Sync" },
      { to: "/portal-push",      label: "Portal Push",      icon: CloudArrowUp,   testid: "nav-portal-push",      superAdmin: true },
      { to: "/reconcile",        label: "ERP Reconcile",    icon: ArrowsClockwise, testid: "nav-reconcile",       superAdmin: true },
      { to: "/inbox",            label: "Adhoc Entry",      icon: Tray,           testid: "nav-adhoc",            superAdmin: true },
      { section: "SAP Integration" },
      { to: "/sap/open-orders-report", label: "SAP Open Order Report", icon: TableIcon, testid: "nav-sap-open-orders", admin: true },
      { to: "/sap/item-site-map", label: "Item → Site Map", icon: MapPin, testid: "nav-item-site-map", admin: true },
      { to: "/sap-mapping",      label: "SAP Mapping",      icon: LinkSimple,     testid: "nav-sap-mapping",      admin: true },
      { to: "/sap/customer-addresses", label: "Customer Addresses", icon: MapPin,   testid: "nav-sap-customer-addresses", admin: true },
      { to: "/sap/address-map",  label: "Overall Address Map", icon: MapPin,    testid: "nav-sap-address-map",  admin: true },
      { to: "/sap/outbound",     label: "SAP Outbound",     icon: Truck,          testid: "nav-sap-outbound",     admin: true },
      { to: "/sap/dispatch-test", label: "SAP Dispatch Test", icon: Package,       testid: "nav-sap-dispatch-test", admin: true },
      { to: "/sap/invoicing",    label: "SAP Invoicing",    icon: Receipt,        testid: "nav-sap-invoicing",    admin: true },
      { to: "/sap-credentials",  label: "SAP Credentials",  icon: ShieldCheck,    testid: "nav-sap-credentials",  admin: true },
      { to: "/sap-health",       label: "SAP Health",       icon: Heartbeat,      testid: "nav-sap-health",       admin: true },
      { section: "Configuration" },
      { to: "/transfer-pricing", label: "Transfer Pricing", icon: CurrencyDollar, testid: "nav-transfer-pricing", admin: false },
      { to: "/customers",        label: "Business Config",  icon: UsersIcon,      testid: "nav-customers",        admin: false },
      { to: "/settings",         label: "Settings",         icon: Gear,           testid: "nav-settings",         superAdmin: true },
      { section: "Users & Access" },
      { to: "/users",            label: "User Admin",       icon: ShieldCheck,    testid: "nav-users",            admin: true },
      { to: "/access-control",   label: "Access Control",   icon: ShieldCheck,    testid: "nav-access-control",   admin: true },
    ],
  },
];


export default function Layout({ children, breadcrumb }) {
  const { user, loading, logout, menuAccessLoaded } = useAuth();
  const nav_router = useNavigate();
  const location = useLocation();
  const [fast, setFast] = useState("");
  const [aiQ, setAiQ] = useState("");
  const [llmStatus, setLlmStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [sapStatus, setSapStatus] = useState(null);
  const [buildInfo, setBuildInfo] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const navRef = useRef(null);
  const [currencyMode, setCurrencyMode] = useState(() => localStorage.getItem("currencyMode") || "");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close any open nav dropdown on outside click
  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openMenu]);

  // Close dropdowns whenever the route changes
  useEffect(() => { setOpenMenu(null); setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  useEffect(() => {
    if (currencyMode) localStorage.setItem("currencyMode", currencyMode);
    else localStorage.removeItem("currencyMode");
    // Broadcast change to listeners
    window.dispatchEvent(new CustomEvent("currencyModeChange", { detail: currencyMode }));
  }, [currencyMode]);

  // Menu access is fetched into the module cache by AuthContext (before guarded routes render).
  // menuAccessLoaded from context re-renders the nav once it resolves.


  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchStatus = () => {
      api.get("/llm/status").then((r) => { if (!cancelled) setLlmStatus(r.data); }).catch(() => {});
      api.get("/sync/status").then((r) => { if (!cancelled) setSyncStatus(r.data); }).catch(() => {});
      api.get("/system/db-status").then((r) => { if (!cancelled) setDbStatus(r.data); }).catch(() => {});
      api.get("/sap/status").then((r) => { if (!cancelled) setSapStatus(r.data); }).catch(() => { if (!cancelled) setSapStatus((prev) => (prev?.ok ? prev : { ok: false, reason: "unreachable" })); });
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    api.get("/version").then((r) => setBuildInfo(r.data)).catch(() => {});
  }, []);

  const dismissLlmBanner = async () => {
    try { await api.post("/llm/status/clear"); } catch (_) { /* ignore */ }
    setLlmStatus({ kind: null, message: null, at: null });
  };

  const runSyncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/sync/run");
      toast.success(`Sync done · ${r.data.po_inserted + r.data.po_updated} POs touched, ${r.data.inv_inserted + r.data.inv_updated} invoices touched`);
      setSyncStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="omh-shell"><div className="omh-content">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;

  const initials = user.username.slice(0, 2).toUpperCase();
  const isSuperAdmin = user.role === "super_admin";
  const isAdmin = user.role === "admin" || isSuperAdmin;
  const roleLabel = roleLabelFor(user.role);
  const roleBadgeColors = roleBadge(user.role);
  const role = user.role;
  // Effective, DB-driven access (falls back to legacy defaults until a role is configured).
  const eff = effective(role, menuAccessLoaded ? getMyAccess() : getMyAccess());
  const groupAllowed = (code) => eff.full || eff.groups.has(code);
  const visibleItems = (group) => {
    // Keep section captions; filter real links by effective access.
    const raw = (group.items || []).filter((it) =>
      it.section ? true : (eff.full ? (!it.superAdmin || isSuperAdmin) : eff.items.has(it.to))
    );
    // Drop any section caption that has no visible link before the next caption/end.
    return raw.filter((it, i) => {
      if (!it.section) return true;
      for (let j = i + 1; j < raw.length; j++) {
        if (raw[j].section) break;
        if (raw[j].to) return true;
      }
      return false;
    });
  };
  const hasLinks = (items) => items.some((it) => it.to);
  const pathActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));

  const onFast = (e) => {
    e.preventDefault();
    if (!fast.trim()) return;
    nav_router(`/pos?q=${encodeURIComponent(fast.trim())}`);
  };

  const onAsk = () => {
    const q = aiQ.trim();
    if (!q) return;
    // Route the question into the dedicated Ask AI page (Insights › Ship Window / Ask), which runs
    // /ms/ask and renders the full answer + item breakdown + export — instead of a small popup.
    nav_router(`/ship-window?ask=${encodeURIComponent(q)}`);
    setAiQ("");
  };

  return (
    <div className="omh-shell jde-shell" data-testid="app-shell">
      {/* ===== TOP BAR ===== */}
      <div className="omh-topbar">
        <button
          className="omh-hamburger"
          data-testid="mobile-nav-toggle"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation menu"
        >
          <List size={22} weight="bold" />
        </button>
        <div className="omh-brand" data-testid="brand">
          <div className="omh-logomark">OH</div>
          <div className="omh-wordmark">
            <span className="omh-wordmark-main">Order Hub</span>
            <span className="omh-wordmark-divider" aria-hidden="true"></span>
            <span className="omh-wordmark-sub">Enterprise</span>
          </div>
        </div>

        <form className="omh-fastpath" onSubmit={onFast}>
          <span className="omh-fastpath-label">FAST PATH</span>
          <input
            data-testid="fastpath-input"
            value={fast}
            onChange={(e) => setFast(e.target.value)}
            placeholder="PO-44521…"
            aria-label="Fast path"
          />
        </form>

        <div className="omh-aibar" style={{ position: "relative" }}>
          <Sparkle size={16} weight="fill" className="omh-aibar-icon" />
          <input
            data-testid="ai-search"
            value={aiQ}
            onChange={(e) => setAiQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onAsk(); }}
            placeholder="Ask Order AI… e.g. open qty for 32-86 fixed mount · how much did we ship in July for DM-0033"
          />
          <button type="button" className="omh-aibar-btn" data-testid="ai-search-btn" onClick={onAsk}>
            <MagnifyingGlass size={13} weight="bold" /> AI
          </button>
        </div>

        <div className="omh-top-actions">
          <button
            className="omh-icon-btn"
            onClick={runSyncNow}
            disabled={syncing}
            title={syncStatus?.at
              ? `Last sync: ${(syncStatus.at || "").slice(0,19).replace("T"," ")} · ${syncStatus.po_inserted ?? 0}+${syncStatus.po_updated ?? 0} POs / ${syncStatus.inv_inserted ?? 0}+${syncStatus.inv_updated ?? 0} invoices`
              : "Sync from SAP (MS SQL)"}
            data-testid="topbar-sync"
            style={{ position: "relative" }}
          >
            <ArrowsClockwise size={16} weight={syncing ? "bold" : "regular"}
              style={syncing ? { animation: "spin 1s linear infinite" } : undefined} />
            {syncStatus?.at && (
              <span style={{
                position: "absolute", bottom: -2, right: -2, width: 6, height: 6,
                background: "#10B981", borderRadius: "50%", border: "1px solid white",
              }} />
            )}
          </button>
          <button className="omh-icon-btn" title="Notifications" data-testid="topbar-notif" style={{ display: "none" }}><Bell size={16} weight="regular" /></button>
          <button className="omh-icon-btn" title="Help" data-testid="topbar-help" style={{ display: "none" }}><Question size={16} weight="regular" /></button>
          {isSuperAdmin && (
            <button className="omh-icon-btn" onClick={() => nav_router("/settings")} title="Settings" data-testid="topbar-settings"><Gear size={16} weight="regular" /></button>
          )}
          {/* Global USD→INR FX rate (Fixed default 80 / Live) — drives every ₹ figure app-wide */}
          <FxControl />
          <div className="omh-user">
            <div className="omh-avatar" data-testid="user-avatar">{initials}</div>
            <div className="omh-user-info">
              <div className="omh-user-name" data-testid="header-user">{user.username}</div>
              <div className="omh-user-role" style={{ marginTop: 2 }}>
                <span
                  data-testid="header-role-badge"
                  style={{
                    display: "inline-block", padding: "1px 8px", borderRadius: 999,
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase",
                    background: roleBadgeColors.bg, color: roleBadgeColors.fg,
                  }}
                >
                  {roleLabel}
                </span>
              </div>
            </div>
            <button onClick={logout} className="omh-icon-btn" title="Sign out" data-testid="logout-btn">
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* ===== MOBILE NAV DRAWER ===== */}
      {drawerOpen && (
        <div className="omh-drawer-overlay" data-testid="mobile-nav-overlay" onClick={() => setDrawerOpen(false)}>
          <aside className="omh-drawer" data-testid="mobile-nav-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="omh-drawer-head">
              <div className="omh-brand">
                <div className="omh-logomark">OH</div>
                <div className="omh-wordmark">
                  <span className="omh-wordmark-main">Order Hub</span>
                </div>
              </div>
              <button className="omh-icon-btn" data-testid="mobile-nav-close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Quick search / AI inside drawer */}
            <div className="omh-drawer-search">
              <form onSubmit={(e) => { onFast(e); setDrawerOpen(false); }} style={{ display: "flex", gap: 8 }}>
                <input
                  data-testid="mobile-fastpath-input"
                  value={fast}
                  onChange={(e) => setFast(e.target.value)}
                  placeholder="Fast path — PO-44521…"
                  className="omh-drawer-input"
                />
              </form>
            </div>

            <nav className="omh-drawer-nav">
              {NAV_GROUPS.map((group) => {
                if (!groupAllowed(group.code)) return null;
                if (group.type === "link") {
                  const { to, label, icon: Icon, testid } = group;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/"}
                      data-testid={`drawer-${testid}`}
                      className={({ isActive }) => "omh-drawer-item" + (isActive ? " active" : "")}
                    >
                      <Icon size={18} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  );
                }
                const items = visibleItems(group);
                if (!hasLinks(items)) return null;
                const { label, icon: Icon } = group;
                return (
                  <div key={group.code} className="omh-drawer-group">
                    <div className="omh-drawer-group-label"><Icon size={14} weight="bold" /> {label}</div>
                    {items.map((it) => {
                      if (it.section) {
                        return (
                          <div key={`dsec-${it.section}`} data-testid={`drawer-section-${it.section}`}
                               style={{ padding: "8px 12px 3px", fontSize: 10, fontWeight: 800, letterSpacing: ".06em",
                                        textTransform: "uppercase", color: "#94A3B8" }}>
                            {it.section}
                          </div>
                        );
                      }
                      const { to, label: itemLabel, icon: ItemIcon, testid: itemTestid } = it;
                      return (
                      <NavLink
                        key={to}
                        to={to}
                        end
                        data-testid={`drawer-${itemTestid}`}
                        className={({ isActive }) => "omh-drawer-item sub" + (isActive ? " active" : "")}
                      >
                        <ItemIcon size={18} weight="bold" />
                        <span>{itemLabel}</span>
                      </NavLink>
                      );
                    })}
                  </div>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* ===== MODULE NAV ===== */}
      <nav className="omh-modnav" ref={navRef}>
        {NAV_GROUPS.map((group) => {
          if (!groupAllowed(group.code)) return null;
          if (group.type === "link") {
            const { to, label, code, icon: Icon, testid } = group;
            return (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testid}
                className={({ isActive }) => "omh-modnav-item" + (isActive ? " active" : "")}
              >
                <Icon size={16} weight="bold" />
                <span className="omh-modnav-label">{label}</span>
                <span className="omh-modnav-code">{code}</span>
              </NavLink>
            );
          }

          // type === "menu" — dropdown of grouped links
          const items = visibleItems(group);
          if (!hasLinks(items)) return null;
          const { label, code, icon: Icon, testid } = group;
          const isOpen = openMenu === code;
          const groupActive = items.some((it) => it.to && pathActive(it.to));
          return (
            <div key={code} className="omh-modnav-group" style={{ position: "relative", display: "flex" }}>
              <button
                type="button"
                data-testid={testid}
                onClick={() => setOpenMenu((cur) => (cur === code ? null : code))}
                className={"omh-modnav-item" + (groupActive ? " active" : "")}
                style={{ background: "none", border: "none", cursor: "pointer", font: "inherit" }}
                aria-haspopup="true"
                aria-expanded={isOpen}
              >
                <Icon size={16} weight="bold" />
                <span className="omh-modnav-label">{label}</span>
                <span className="omh-modnav-code">{code}</span>
                <CaretDown size={10} weight="bold" className="omh-modnav-caret"
                  style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
              </button>
              {isOpen && (
                <div
                  data-testid={`${testid}-dropdown`}
                  style={{
                    position: "absolute", top: "100%", left: 0, minWidth: 210, zIndex: 60,
                    background: "#fff", border: "1px solid var(--omh-border, #E2E8F0)", borderRadius: 8,
                    boxShadow: "0 12px 32px rgba(2, 8, 23, 0.22)", overflowX: "hidden", overflowY: "auto",
                    maxHeight: "min(74vh, 600px)", padding: 4, marginTop: 2,
                  }}
                >
                  {items.map((it, idx) => {
                    if (it.section) {
                      return (
                        <div key={`sec-${it.section}`} data-testid={`${testid}-section-${it.section}`}
                             style={{
                               padding: idx === 0 ? "6px 12px 4px" : "8px 12px 4px",
                               fontSize: 10, fontWeight: 800, letterSpacing: ".06em",
                               textTransform: "uppercase", color: "#94A3B8", whiteSpace: "nowrap",
                               borderTop: idx === 0 ? "none" : "1px solid var(--omh-border, #E2E8F0)",
                               marginTop: idx === 0 ? 0 : 4,
                             }}>
                          {it.section}
                        </div>
                      );
                    }
                    const { to, label: itemLabel, icon: ItemIcon, testid: itemTestid } = it;
                    return (
                    <NavLink
                      key={to}
                      to={to}
                      end
                      data-testid={itemTestid}
                      onClick={() => setOpenMenu(null)}
                      className={({ isActive }) => "omh-dropdown-item" + (isActive ? " active" : "")}
                      style={({ isActive }) => ({
                        display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                        borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: 600,
                        color: isActive ? "var(--omh-primary, #0EA5E9)" : "#1E293B",
                        background: isActive ? "rgba(6,182,212,0.10)" : "transparent",
                        whiteSpace: "nowrap",
                      })}
                    >
                      <ItemIcon size={16} weight="bold" />
                      {itemLabel}
                    </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* ===== PERMANENT ERP CONNECTION STATUS (far right) ===== */}
        {(() => {
          const down = dbStatus && !dbStatus.any_reachable;
          const fb = dbStatus && dbStatus.on_fallback;
          const label = !dbStatus ? "ERP \u2026" : (down ? "ERP Link Down" : (fb ? "Fallback Server \u00b7 Live" : "Primary Server \u00b7 Live"));
          const dot = !dbStatus ? "#94A3B8" : (down ? "#F87171" : (fb ? "#FBBF24" : "#34D399"));
          const bg = down ? "rgba(220,38,38,0.16)" : (fb ? "rgba(245,158,11,0.16)" : "rgba(16,185,129,0.15)");
          return (
            <div
              data-testid="erp-status-badge"
              title={dbStatus
                ? `VM Connection: ${dbStatus.active || dbStatus.primary || "none"}${dbStatus.on_fallback ? " (on fallback)" : ""}\nPrimary: ${dbStatus.primary} | Fallback: ${dbStatus.fallback || "none"} | Active: ${dbStatus.active || "none"}`
                : "Checking ERP connection\u2026"}
              style={{
                marginLeft: "auto", alignSelf: "center", display: "inline-flex", alignItems: "center",
                gap: 7, padding: "4px 12px", borderRadius: 999, background: bg,
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: 999, background: dot,
                boxShadow: `0 0 6px ${dot}`, flex: "0 0 auto",
              }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2, color: "#E2E8F0", whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
          );
        })()}

        {/* ===== PERMANENT SAP (ByDesign) CONNECTION STATUS ===== */}
        {(() => {
          const ok = sapStatus && sapStatus.ok;
          const label = !sapStatus ? "SAP \u2026" : (ok ? "SAP \u00b7 Connected" : "SAP Link Down");
          const dot = !sapStatus ? "#94A3B8" : (ok ? "#34D399" : "#F87171");
          const bg = !sapStatus ? "rgba(148,163,184,0.15)" : (ok ? "rgba(16,185,129,0.15)" : "rgba(220,38,38,0.16)");
          const tip = !sapStatus ? "Checking SAP connection\u2026"
            : (ok ? `SAP ByDesign reachable${sapStatus.tenant ? " \u2014 " + sapStatus.tenant : ""}`
                  : `SAP unavailable: ${sapStatus.reason || ("HTTP " + (sapStatus.http_status || "?"))}`);
          return (
            <div
              data-testid="sap-status-badge"
              title={tip}
              style={{
                marginLeft: 8, alignSelf: "center", display: "inline-flex", alignItems: "center",
                gap: 7, padding: "4px 12px", borderRadius: 999, background: bg,
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: 999, background: dot,
                boxShadow: `0 0 6px ${dot}`, flex: "0 0 auto",
              }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2, color: "#E2E8F0", whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
          );
        })()}
      </nav>

      {/* ===== LLM BUDGET BANNER ===== */}
      {llmStatus?.kind === "budget_exceeded" && (
        <div
          data-testid="llm-budget-banner"
          style={{
            background: "#FEF3C7",
            borderTop: "1px solid #F59E0B",
            borderBottom: "1px solid #F59E0B",
            color: "#92400E",
            padding: "8px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <WarningOctagon size={14} weight="fill" />
          <span>
            AI extraction is blocked — Emergent Universal Key budget exceeded.
            Top up in <strong>app.emergent.sh → Profile → Universal Key → Add Balance</strong> to resume PO processing.
          </span>
          <span style={{ flex: 1 }} />
          {llmStatus.at && (
            <span style={{ opacity: 0.8, fontWeight: 400 }}>
              last error: {llmStatus.at.slice(0, 19).replace("T", " ")}
            </span>
          )}
          <button
            onClick={dismissLlmBanner}
            className="omh-icon-btn"
            title="Dismiss"
            data-testid="llm-banner-dismiss"
            style={{ color: "#92400E" }}
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {/* ===== PORTAL DB OUTAGE BANNER (RED only — no connectivity) ===== */}
      {/* The amber "failover active" state is intentionally NOT shown here — the far-right
          ERP status badge (with hover tooltip) already surfaces it. We only interrupt the
          user with a banner when BOTH portal servers are unreachable. */}
      {dbStatus && !dbStatus.any_reachable && (
        <div
          data-testid="db-failover-banner"
          style={{
            background: "#FEE2E2",
            borderTop: "1px solid #DC2626",
            borderBottom: "1px solid #DC2626",
            color: "#991B1B",
            padding: "8px 16px", display: "flex", alignItems: "center", gap: 10,
            fontSize: 12, fontWeight: 600,
          }}
        >
          <WarningOctagon size={14} weight="fill" />
          <span>
            Both portal database servers (<strong>{dbStatus.primary}</strong> and{" "}
            <strong>{dbStatus.fallback}</strong>) are currently unreachable. Order/PO screens work, but
            live Dispatch/Shipping data is temporarily unavailable. IT has been notified — retrying automatically.
          </span>
        </div>
      )}

      {/* ===== BREADCRUMB ===== */}
      <div className="omh-breadcrumb" data-testid="breadcrumb">
        <NavLink to="/" className="omh-breadcrumb-home"><House size={13} weight="bold" /></NavLink>
        <span className="omh-breadcrumb-sep">›</span>
        <span>Home</span>
        {breadcrumb?.map((b, i) => (
          <span key={i} style={{ display: "contents" }}>
            <span className="omh-breadcrumb-sep">›</span>
            <span>{b}</span>
          </span>
        ))}
        <div style={{ marginLeft: "auto" }}><RefreshData /></div>
      </div>

      <main className="omh-content jde-content">{children}</main>

      {/* ===== BUILD VERSION FOOTER ===== */}
      <footer
        data-testid="app-version-footer"
        style={{
          padding: "10px 16px", borderTop: "1px solid #E5E7EB", background: "#FAFAFA",
          color: "#9CA3AF", fontSize: 11, display: "flex", justifyContent: "flex-end",
          gap: 12, letterSpacing: "0.02em",
        }}
      >
        <span>Order Hub</span>
        {buildInfo && (
          <span data-testid="app-version-build" title={`SHA ${buildInfo.sha}\nBuilt ${buildInfo.build_time}\nStarted ${buildInfo.started_at}`}>
            build <strong data-testid="app-version-sha" style={{ color: "#6B7280", fontWeight: 600 }}>{buildInfo.short_sha}</strong>
            {buildInfo.build_time && buildInfo.build_time !== "unknown" && (
              <> · {String(buildInfo.build_time).replace("T", " ").slice(0, 16)}</>
            )}
          </span>
        )}
      </footer>
    </div>
  );
}
